<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAccount } from 'dashboard/composables/useAccount';
import OnboardingView from '../OnboardingView.vue';
import EmptyStateMessage from './EmptyStateMessage.vue';

const props = defineProps({
  isOnExpandedLayout: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();
const { accountScopedUrl } = useAccount();

const currentChat = computed(() => store.getters.getSelectedChat);
const allConversations = computed(() => store.getters.getAllConversations);
const inboxesList = computed(() => store.getters['inboxes/getInboxes']);
const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);
const loadingChatList = computed(() => store.getters.getChatListLoadingStatus);

const loadingIndicatorMessage = computed(() => {
  if (uiFlags.value.isFetching) {
    return t('CONVERSATION.LOADING_INBOXES');
  }
  return t('CONVERSATION.LOADING_CONVERSATIONS');
});

const conversationMissingMessage = computed(() => {
  if (!props.isOnExpandedLayout) {
    return t('CONVERSATION.SELECT_A_CONVERSATION');
  }
  return t('CONVERSATION.404');
});

// eslint-disable-next-line no-unused-vars
const newInboxURL = computed(() => {
  return accountScopedUrl('settings/inboxes/new');
});

const emptyClassName = computed(() => {
  if (
    !inboxesList.value.length &&
    !uiFlags.value.isFetching &&
    !loadingChatList.value &&
    isAdmin.value
  ) {
    return 'h-full overflow-auto w-full';
  }
  return 'flex-1 min-w-0 px-0 flex flex-col items-center justify-center h-full bg-background';
});
</script>

<template>
  <div :class="emptyClassName">
    <woot-loading-state
      v-if="uiFlags.isFetching || loadingChatList"
      :message="loadingIndicatorMessage"
    />
    <!-- No inboxes attached -->
    <div
      v-if="!inboxesList.length && !uiFlags.isFetching && !loadingChatList"
      class="clearfix mx-auto"
    >
      <OnboardingView v-if="isAdmin" />
      <EmptyStateMessage v-else :message="$t('CONVERSATION.NO_INBOX_AGENT')" />
    </div>
    <!-- Show empty state images if not loading -->

    <div
      v-else-if="!uiFlags.isFetching && !loadingChatList"
      class="flex flex-col items-center justify-center h-full"
    >
      <!-- No conversations available -->
      <EmptyStateMessage
        v-if="!allConversations.length"
        :message="$t('CONVERSATION.NO_MESSAGE_1')"
      />
      <EmptyStateMessage
        v-else-if="allConversations.length && !currentChat.id"
        :message="conversationMissingMessage"
      />
    </div>
  </div>
</template>
